<template>
  <MultiSelect
    v-if="multiple"
    :model-value="selectedValues"
    :options="displayOptions"
    option-label="label"
    option-value="value"
    display="chip"
    filter
    filter-placeholder="Search..."
    :loading="isLoading"
    :disabled="isLoading"
    :placeholder="placeholder"
    :aria-label="ariaLabel"
    class="max-w-full min-w-56"
    @update:model-value="emit('update:modelValue', $event)" />
  <Select
    v-else
    :model-value="selectedValue"
    :options="displayOptions"
    option-label="label"
    option-value="value"
    filter
    filter-placeholder="Search..."
    :loading="isLoading"
    :disabled="isLoading"
    :placeholder="placeholder"
    :aria-label="ariaLabel"
    class="max-w-full min-w-56"
    @update:model-value="emit('update:modelValue', $event)" />
</template>

<script setup lang="ts">
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import { computed, onMounted, ref, type PropType } from 'vue'

type OptionValue = string | number

interface Option {
  label: string
  value: OptionValue
}

const props = defineProps({
  modelValue: { type: [String, Number, Array] as PropType<OptionValue | OptionValue[] | null>, default: undefined },
  /** Loads the choices once on mount; integration lists are fetched, local ones read from stores. */
  options: { type: Function as PropType<() => Option[] | Promise<Option[]>>, required: true },
  multiple: { type: Boolean, default: false },
  placeholder: { type: String, default: undefined },
  ariaLabel: { type: String, default: undefined }
})

const emit = defineEmits<{ 'update:modelValue': [value: OptionValue | OptionValue[] | null] }>()

const loadedOptions = ref<Option[]>([])
const isLoading = ref(true)

onMounted(async () => {
  try {
    loadedOptions.value = await props.options()
  } catch (error) {
    console.error('Error loading options:', error)
    loadedOptions.value = []
  } finally {
    isLoading.value = false
  }
})

// Saved IDs may be numbers while the platform lists strings (or the reverse); match them loosely.
const findOption = (value: OptionValue) =>
  loadedOptions.value.find((option) =>
    typeof option.value === typeof value ? option.value === value : String(option.value) === String(value)
  )

const isBlank = (value: unknown) => value === null || value === undefined || value === ''

const normalize = (value: OptionValue) => findOption(value)?.value ?? value

const selectedValues = computed(() => (Array.isArray(props.modelValue) ? props.modelValue.map(normalize) : []))

const selectedValue = computed(() =>
  isBlank(props.modelValue) || Array.isArray(props.modelValue) ? null : normalize(props.modelValue as OptionValue)
)

// A saved choice the platform no longer lists stays visible (and removable) instead of vanishing.
const displayOptions = computed(() => {
  if (isLoading.value) {
    return loadedOptions.value
  }
  const saved = props.multiple ? selectedValues.value : isBlank(selectedValue.value) ? [] : [selectedValue.value]
  const missing = saved
    .filter((value): value is OptionValue => value !== null && !findOption(value))
    .map((value) => ({ label: `Unknown (${value})`, value }))
  return [...loadedOptions.value, ...missing]
})
</script>
